import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  CalendarIcon, 
  ClockIcon, 
  CheckCircleIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { getPublicDoctors, getPublicDoctorSlots, submitBookingRequest } from '../../api/publicBooking';

// Available departments/services for booking (fallback when no doctor selected)
const departments = [
  { id: 'cardiology', name: 'Cardiology', icon: '🫀' },
  { id: 'neurology', name: 'Neurology', icon: '🧠' },
  { id: 'general-medicine', name: 'General Medicine', icon: '👨‍⚕️' },
  { id: 'pediatrics', name: 'Pediatrics', icon: '👶' },
  { id: 'orthopedics', name: 'Orthopedics', icon: '🦴' },
  { id: 'ophthalmology', name: 'Ophthalmology', icon: '👁️' },
  { id: 'dermatology', name: 'Dermatology', icon: '🩺' },
  { id: 'gynecology', name: 'Gynecology', icon: '👩‍⚕️' },
];

// Preferred time slots (fallback when no slots available)
const timeSlotOptions = [
  { id: 'morning', label: 'Morning (9 AM - 12 PM)', value: 'morning' },
  { id: 'afternoon', label: 'Afternoon (12 PM - 4 PM)', value: 'afternoon' },
  { id: 'evening', label: 'Evening (4 PM - 7 PM)', value: 'evening' },
];

const PublicBookingForm = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  
  // Doctors and slots data
  const [doctors, setDoctors] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    mobile: '',
    email: '',
    doctorId: '',
    department: '',
    preferredDate: '',
    preferredTime: '',
    selectedSlot: null,
    reason: '',
  });
  
  const [errors, setErrors] = useState({});
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingRef, setBookingRef] = useState('');

  // Load doctors on mount
  useEffect(() => {
    if (isOpen) {
      loadDoctors();
    }
  }, [isOpen]);

  // Load slots when doctor and date are selected
  useEffect(() => {
    if (formData.doctorId && formData.preferredDate) {
      loadSlots();
    }
  }, [formData.doctorId, formData.preferredDate]);

  const loadDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await getPublicDoctors();
      if (response.success && response.data) {
        setDoctors(response.data);
      }
    } catch (error) {
      console.error('Failed to load doctors:', error);
    } finally {
      setLoadingDoctors(false);
    }
  };

  const loadSlots = async () => {
    if (!formData.doctorId || !formData.preferredDate) return;
    
    setLoadingSlots(true);
    setAvailableSlots([]);
    
    try {
      const response = await getPublicDoctorSlots(formData.doctorId, formData.preferredDate);
      if (response.success && response.data?.slots) {
        setAvailableSlots(response.data.slots);
      }
    } catch (error) {
      console.error('Failed to load slots:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear slot selection when date changes
    if (field === 'preferredDate') {
      setFormData(prev => ({ ...prev, selectedSlot: null, preferredTime: '' }));
    }
    
    // Clear date and slot when doctor changes
    if (field === 'doctorId') {
      setFormData(prev => ({ 
        ...prev, 
        preferredDate: '', 
        selectedSlot: null, 
        preferredTime: '',
        department: ''
      }));
      setAvailableSlots([]);
    }
  };

  const handleSlotSelect = (slot) => {
    setFormData(prev => ({ 
      ...prev, 
      selectedSlot: slot,
      preferredTime: '' // Clear generic time when specific slot is selected
    }));
    if (errors.selectedSlot || errors.preferredTime) {
      setErrors(prev => ({ ...prev, selectedSlot: '', preferredTime: '' }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.mobile.replace(/\s/g, ''))) {
      newErrors.mobile = 'Please enter a valid 10-digit mobile number';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    // Either doctor or department must be selected
    if (!formData.doctorId && !formData.department) {
      newErrors.doctorId = 'Please select a doctor or department';
    }
    
    if (!formData.preferredDate) {
      newErrors.preferredDate = 'Please select a preferred date';
    }
    
    // Either a slot or a time preference must be selected
    if (!formData.selectedSlot && !formData.preferredTime) {
      newErrors.preferredTime = 'Please select a time slot or preferred time';
    }
    
    if (!formData.reason.trim()) {
      newErrors.reason = 'Please describe your reason for visit';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    
    try {
      const requestData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        mobile: formData.mobile.replace(/\s/g, ''),
        email: formData.email?.trim() || null,
        doctorId: formData.doctorId || null,
        department: formData.department || null,
        preferredDate: formData.preferredDate,
        preferredTime: formData.preferredTime || null,
        slotId: formData.selectedSlot?._id || null,
        reason: formData.reason.trim()
      };

      const response = await submitBookingRequest(requestData);
      
      if (response.success) {
        setBookingRef(response.data.referenceNumber);
        setBookingComplete(true);
        setStep(3);
      } else {
        setErrors({ submit: response.message || 'Failed to submit booking request. Please try again.' });
      }
    } catch (error) {
      console.error('Booking error:', error);
      setErrors({ submit: 'Failed to submit booking request. Please try again or call us directly.' });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      firstName: '',
      lastName: '',
      mobile: '',
      email: '',
      doctorId: '',
      department: '',
      preferredDate: '',
      preferredTime: '',
      selectedSlot: null,
      reason: '',
    });
    setErrors({});
    setBookingComplete(false);
    setBookingRef('');
    setAvailableSlots([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Get minimum date (today)
  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get maximum date (30 days from now)
  const getMaxDate = () => {
    const maxDate = new Date();
    maxDate.setDate(maxDate.getDate() + 30);
    return maxDate.toISOString().split('T')[0];
  };

  // Get selected doctor info
  const getSelectedDoctor = () => {
    return doctors.find(d => d._id === formData.doctorId || d.id === formData.doctorId);
  };

  if (!isOpen) return null;

  return (
    <div className="w-full">
      {/* Progress Steps */}
      <div className="flex items-center justify-center mb-6">
        {[1, 2, 3].map((stepNumber) => (
          <div key={stepNumber} className="flex items-center">
            <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
              step >= stepNumber 
                ? 'bg-[#2D869C] text-white' 
                : 'bg-gray-200 text-gray-500'
            }`}>
              {step > stepNumber ? (
                <CheckCircleIcon className="w-5 h-5" />
              ) : (
                stepNumber
              )}
            </div>
            {stepNumber < 3 && (
              <div className={`w-12 sm:w-20 h-1 mx-1.5 transition-colors ${
                step > stepNumber ? 'bg-[#2D869C]' : 'bg-gray-200'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex justify-between mb-6 text-xs text-gray-500">
        <span className={step >= 1 ? 'text-[#2D869C] font-medium' : ''}>Your Details</span>
        <span className={step >= 2 ? 'text-[#2D869C] font-medium' : ''}>Appointment</span>
        <span className={step >= 3 ? 'text-[#2D869C] font-medium' : ''}>Confirmation</span>
      </div>

      <AnimatePresence mode="wait">
        {/* Step 1: Patient Information */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition text-sm ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mobile Number *
              </label>
              <div className="flex">
                <span className="inline-flex items-center px-3 py-2.5 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-lg text-sm">
                  +91
                </span>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value.replace(/\D/g, '').slice(0, 10))}
                  className={`flex-1 px-4 py-2.5 border rounded-r-lg focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] transition text-sm ${
                    errors.mobile ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Enter 10-digit mobile number"
                />
              </div>
              {errors.mobile && (
                <p className="text-red-500 text-xs mt-1">{errors.mobile}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Address (Optional)
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] transition text-sm ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Enter email for confirmation"
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 2: Appointment Details */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            {/* Doctor Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Doctor {doctors.length > 0 && '*'}
              </label>
              {loadingDoctors ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2D869C]"></div>
                  <span className="ml-2 text-sm text-gray-600">Loading doctors...</span>
                </div>
              ) : doctors.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {doctors.map((doctor) => (
                    <button
                      key={doctor._id || doctor.id}
                      type="button"
                      onClick={() => handleInputChange('doctorId', doctor._id || doctor.id)}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${
                        formData.doctorId === (doctor._id || doctor.id)
                          ? 'border-[#2D869C] bg-[#9DC9D4]/10'
                          : 'border-gray-200 hover:border-[#9DC9D4]'
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm">{doctor.name}</div>
                      <div className="text-xs text-[#2D869C]">{doctor.specialty}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mb-4">
                  <p className="text-sm text-gray-500 mb-2">Select a department:</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {departments.map((dept) => (
                      <button
                        key={dept.id}
                        type="button"
                        onClick={() => handleInputChange('department', dept.id)}
                        className={`p-2 border-2 rounded-lg text-center transition-all ${
                          formData.department === dept.id
                            ? 'border-[#2D869C] bg-[#9DC9D4]/10 text-[#2D869C]'
                            : 'border-gray-200 hover:border-[#9DC9D4]'
                        }`}
                      >
                        <span className="text-xl block">{dept.icon}</span>
                        <span className="text-xs font-medium">{dept.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {errors.doctorId && (
                <p className="text-red-500 text-xs mt-1">{errors.doctorId}</p>
              )}
            </div>

            {/* Date Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Preferred Date *
              </label>
              <input
                type="date"
                value={formData.preferredDate}
                onChange={(e) => handleInputChange('preferredDate', e.target.value)}
                min={getMinDate()}
                max={getMaxDate()}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] transition text-sm ${
                  errors.preferredDate ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.preferredDate && (
                <p className="text-red-500 text-xs mt-1">{errors.preferredDate}</p>
              )}
            </div>

            {/* Time Slots */}
            {formData.preferredDate && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time Slot *
                </label>
                
                {loadingSlots ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#2D869C]"></div>
                    <span className="ml-2 text-sm text-gray-600">Loading available slots...</span>
                  </div>
                ) : availableSlots.length > 0 ? (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-32 overflow-y-auto">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot._id || slot.id}
                        type="button"
                        onClick={() => handleSlotSelect(slot)}
                        className={`p-2 border-2 rounded-lg text-center transition-all text-xs ${
                          formData.selectedSlot?._id === slot._id
                            ? 'border-[#2D869C] bg-[#9DC9D4]/10 text-[#2D869C]'
                            : 'border-gray-200 hover:border-[#9DC9D4]'
                        }`}
                      >
                        <ClockIcon className="w-4 h-4 mx-auto mb-0.5 text-gray-500" />
                        {slot.formattedTime?.split(' - ')[0] || 'Time'}
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <ExclamationCircleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm text-yellow-800 font-medium">No slots available online</p>
                        <p className="text-xs text-yellow-700 mt-1">
                          Select a preferred time and we'll call you to confirm.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Fallback time preference (when no specific slots) */}
                {(availableSlots.length === 0 || !formData.doctorId) && (
                  <div className="mt-2">
                    <label className="block text-xs text-gray-600 mb-1.5">Preferred Time</label>
                    <div className="grid grid-cols-3 gap-2">
                      {timeSlotOptions.map((slot) => (
                        <button
                          key={slot.id}
                          type="button"
                          onClick={() => handleInputChange('preferredTime', slot.value)}
                          className={`p-2 border-2 rounded-lg text-center transition-all text-xs ${
                            formData.preferredTime === slot.value
                              ? 'border-[#2D869C] bg-[#9DC9D4]/10 text-[#2D869C]'
                              : 'border-gray-200 hover:border-[#9DC9D4]'
                          }`}
                        >
                          {slot.label.split(' ')[0]}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {errors.preferredTime && (
                  <p className="text-red-500 text-xs mt-1">{errors.preferredTime}</p>
                )}
              </div>
            )}

            {/* Reason for Visit */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Visit *
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                rows={2}
                className={`w-full px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] transition resize-none text-sm ${
                  errors.reason ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Briefly describe your symptoms or reason for consultation"
              />
              {errors.reason && (
                <p className="text-red-500 text-xs mt-1">{errors.reason}</p>
              )}
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirmation */}
        {step === 3 && bookingComplete && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-4"
          >
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircleIcon className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Booking Request Submitted!
            </h3>
            <p className="text-gray-600 text-sm mb-4">
              Our team will contact you shortly to confirm your appointment.
            </p>
            
            <div className="bg-[#9DC9D4]/10 rounded-lg p-4 max-w-sm mx-auto mb-4">
              <div className="text-xs text-[#2D869C] font-medium mb-1">Reference Number</div>
              <div className="text-xl font-bold text-[#2D869C]">{bookingRef}</div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 max-w-sm mx-auto text-left">
              <h4 className="font-semibold text-gray-900 mb-2 text-sm">Booking Summary</h4>
              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-medium">{formData.firstName} {formData.lastName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile:</span>
                  <span className="font-medium">+91 {formData.mobile}</span>
                </div>
                {getSelectedDoctor() && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Doctor:</span>
                    <span className="font-medium">{getSelectedDoctor().name}</span>
                  </div>
                )}
                {formData.department && !formData.doctorId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Department:</span>
                    <span className="font-medium">
                      {departments.find(d => d.id === formData.department)?.name}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Date:</span>
                  <span className="font-medium">
                    {formData.preferredDate && new Date(formData.preferredDate).toLocaleDateString('en-IN', {
                      weekday: 'short',
                      day: 'numeric',
                      month: 'short'
                    })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Time:</span>
                  <span className="font-medium">
                    {formData.selectedSlot 
                      ? formData.selectedSlot.formattedTime 
                      : timeSlotOptions.find(t => t.value === formData.preferredTime)?.label || 'To be confirmed'}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              For immediate assistance, call <a href="tel:02240709812" className="text-[#2D869C] font-medium hover:text-[#9DC9D4]">022-40709812</a>
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Error Message */}
      {errors.submit && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm flex items-start gap-2">
          <ExclamationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span>{errors.submit}</span>
        </div>
      )}

      {/* Footer Buttons */}
      <div className="flex justify-between mt-6 pt-4 border-t border-gray-200">
        <button
          onClick={step === 1 ? handleClose : () => setStep(step - 1)}
          className="px-5 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition font-medium text-sm"
          disabled={loading}
        >
          {step === 1 ? 'Cancel' : 'Back'}
        </button>

        {step < 3 && (
          <button
            onClick={handleNext}
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white rounded-full hover:shadow-lg transition-all font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Processing...
              </>
            ) : step === 2 ? (
              'Submit Request'
            ) : (
              'Next'
            )}
          </button>
        )}

        {step === 3 && (
          <button
            onClick={handleClose}
            className="px-6 py-2 bg-gradient-to-r from-[#2D869C] to-[#9DC9D4] text-white rounded-full hover:shadow-lg transition-all font-medium text-sm"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
};

export default PublicBookingForm;
